import { useState } from 'react';
import clsx from 'clsx';
import {
  setReparatieIntent,
  clearReparatieIntent,
  type ReparatieIntentStatus,
} from '../../lib/reparatie';

interface Props {
  wiId: string;
  wiName: string;
  initialKlantverzoek: string | null;
  initialStatus: ReparatieIntentStatus | null;
  initialNotitie: string | null;
  onClose: () => void;
  onSaved: () => void;
}

const STATUS_LABEL: Record<ReparatieIntentStatus, string> = {
  Open: '○ Open',
  Bevestigd: '● Bevestigd',
  Niet_Nodig: '○ Niet nodig',
};

const STATUS_BADGE: Record<ReparatieIntentStatus, string> = {
  Open: 'bg-amber-100 text-amber-800 border border-amber-300',
  Bevestigd: 'bg-emerald-100 text-emerald-800 border border-emerald-300',
  Niet_Nodig: 'bg-slate-100 text-slate-600 border border-slate-300',
};

export function ReparatieIntentDialog({
  wiId,
  wiName,
  initialKlantverzoek,
  initialStatus,
  initialNotitie,
  onClose,
  onSaved,
}: Props) {
  const [tekst, setTekst] = useState(initialKlantverzoek ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isExisting = !!(initialKlantverzoek && initialKlantverzoek.trim().length > 0);

  const canSave = !submitting && tekst.trim().length > 0;

  const handleSave = async () => {
    if (!canSave) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await setReparatieIntent({ wiId, klantverzoek: tekst.trim() });
      if (!res.success) {
        setError(res.message || 'Opslaan mislukt');
        setSubmitting(false);
        return;
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opslaan mislukt');
      setSubmitting(false);
    }
  };

  const handleClear = async () => {
    if (submitting) return;
    if (!window.confirm('Weet je zeker dat je het klantverzoek wilt wissen?')) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await clearReparatieIntent(wiId);
      if (!res.success) {
        setError(res.message || 'Wissen mislukt');
        setSubmitting(false);
        return;
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Wissen mislukt');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              Klantverzoek reparatie
            </h2>
            <p className="text-sm text-slate-500">{wiName}</p>
          </div>
          {initialStatus && (
            <span
              className={clsx(
                'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium',
                STATUS_BADGE[initialStatus]
              )}
            >
              {STATUS_LABEL[initialStatus]}
            </span>
          )}
        </div>

        <div className="space-y-4 px-5 py-4">
          <p className="text-sm text-slate-600">
            Leg vast wat de klant wil laten repareren. De inspecteur ziet dit
            tijdens voorinspectie en bevestigt of weerlegt.
          </p>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Wat wil de klant laten repareren?
            </label>
            <textarea
              value={tekst}
              onChange={(e) => setTekst(e.target.value.slice(0, 1024))}
              rows={4}
              maxLength={1024}
              autoFocus
              placeholder="Bijv. kwastjes opnieuw knopen aan de korte kant"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              disabled={submitting}
            />
            <div className="mt-1 text-right text-xs text-slate-400">
              {tekst.length} / 1024
            </div>
          </div>

          {initialNotitie && (
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
              <div className="mb-1 text-xs font-medium uppercase text-slate-500">
                Inspecteur
              </div>
              <div className="whitespace-pre-line text-slate-700">{initialNotitie}</div>
            </div>
          )}

          {error && (
            <div className="rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-800">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
          <div>
            {isExisting && (
              <button
                type="button"
                onClick={handleClear}
                disabled={submitting}
                className="rounded-md border border-rose-300 bg-white px-3 py-1.5 text-sm font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50"
              >
                Wissen
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Annuleren
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave}
              className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {submitting ? 'Opslaan…' : 'Opslaan'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
